import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface PlanUsageItem {
  label: string;
  serviceKey: string;
  serviceName: string;
  used: number;
  cap: number | null; // null = unlimited
  creditBalance: number;
  isUsingCredits: boolean;
  isTrial: boolean;
  limitType: string; // 'unlimited' | 'monthly' | 'active' | 'total'
}

interface PlanUsageResult {
  planName: string | null;
  items: PlanUsageItem[];
  isLoading: boolean;
}

interface PlanUsageEntitlement {
  serviceKey: string;
  serviceName: string;
  used: number;
  limit: number | null;
  creditBalance: number;
  mode: string;
  reason: string;
  isTrial: boolean;
  limitType: string;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function numericValue(value: unknown, fallback: number): number {
  return typeof value === 'number' ? value : fallback;
}

/**
 * Dynamically fetches plan-subscribed services and their entitlement usage.
 * Only shows services included in the org's active plan/bundle.
 */
export function usePlanUsage(): PlanUsageResult {
  const { profile } = useAuth();
  const orgId = profile?.org_id;

  const { data, isLoading } = useQuery({
    queryKey: ['plan-usage', orgId],
    queryFn: async () => {
      // 1. Get active subscription with plan info
      const { data: sub } = await supabase
        .from('organization_subscriptions')
        .select('plan_id, plans!organization_subscriptions_plan_id_fkey(plan_name)')
        .eq('org_id', orgId!)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      const planName = sub?.plans?.plan_name ?? null;
      const planId = sub?.plan_id;

      if (!planId) {
        return { planName, entitlements: [] };
      }

      // 2. Fetch enabled services for this plan
      const { data: planServices, error: psErr } = await supabase
        .from('plan_services')
        .select('service_id, usage_limit, limit_type, services:service_id(service_key, service_name)')
        .eq('plan_id', planId)
        .eq('is_enabled', true);

      if (psErr || !planServices?.length) {
        return { planName, entitlements: [] };
      }

      // 3. Check entitlements for each subscribed service
      const entitlements = await Promise.all(
        planServices.map(async (ps): Promise<PlanUsageEntitlement | null> => {
          const svc = ps.services;
          if (!svc?.service_key) return null;

          const { data: result, error } = await supabase.rpc('check_entitlement', {
            _owner_type: 'organization',
            _owner_id: orgId!,
            _service_key: svc.service_key,
            _units_requested: 1,
          });

          if (error || !result) {
            return {
              serviceKey: svc.service_key,
              serviceName: svc.service_name,
              used: 0,
              limit: ps.usage_limit ?? null,
              creditBalance: 0,
              mode: 'hard_block',
              reason: 'backend_blocked',
              isTrial: false,
              limitType: ps.limit_type || 'monthly',
            };
          }

          const r = asRecord(result);
          if (!r) {
            return {
              serviceKey: svc.service_key,
              serviceName: svc.service_name,
              used: 0,
              limit: ps.usage_limit ?? null,
              creditBalance: 0,
              mode: 'hard_block',
              reason: 'backend_blocked',
              isTrial: false,
              limitType: ps.limit_type || 'monthly',
            };
          }

          const reportedLimit = numericValue(r.limit, ps.usage_limit ?? -1);
          return {
            serviceKey: svc.service_key,
            serviceName: svc.service_name,
            used: numericValue(r.used, 0),
            limit: reportedLimit === -1 ? null : reportedLimit,
            creditBalance: numericValue(r.credit_balance, 0),
            mode: typeof r.mode === 'string' ? r.mode : 'hard_block',
            reason: typeof r.reason === 'string' ? r.reason : 'backend_blocked',
            isTrial: r.is_trial === true,
            limitType: typeof r.limit_type === 'string' ? r.limit_type : (ps.limit_type || 'monthly'),
          };
        })
      );

      return {
        planName,
        entitlements: entitlements.filter((entitlement): entitlement is PlanUsageEntitlement => entitlement !== null),
      };
    },
    enabled: !!orgId,
    staleTime: 60_000,
  });

  const items: PlanUsageItem[] = (data?.entitlements ?? []).map((e) => ({
    label: e.serviceKey,
    serviceKey: e.serviceKey,
    serviceName: e.serviceName,
    used: e.used,
    cap: e.limit,
    creditBalance: e.creditBalance,
    isUsingCredits: e.mode === 'credit_topup',
    isTrial: e.isTrial,
    limitType: e.limitType || 'monthly',
  }));

  return {
    planName: data?.planName ?? null,
    items,
    isLoading,
  };
}
